import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { euro, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Leaf, Check, LogOut, ArrowLeft } from "lucide-react";

export default function Billing() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [stripeReady, setStripeReady] = useState(true);

  useEffect(() => { api.get("/billing/plans").then(({ data }) => setPlans(data)); }, []);
  useEffect(() => {
    api.get("/billing/config")
      .then(({ data }) => setStripeReady(!!data.publishable_key))
      .catch(() => setStripeReady(false));
  }, []);

  const isActive = user?.company_status === "active";
  const currentPlan = user?.plan;

  const subscribe = async (key) => {
    if (!stripeReady) { toast.error("Pagamenti non configurati. Contatta l'assistenza."); return; }
    setLoading(key);
    try {
      const { data } = await api.post("/billing/checkout", { plan: key, cycle, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(""); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] px-6 py-12 noise-overlay">
      <div className="max-w-4xl mx-auto">
        {!isActive && (
          <div data-testid="billing-block-banner" className="mb-8 bg-alert/10 border border-alert/30 rounded-sm p-5">
            <h2 className="font-serif text-2xl text-stone-900">{user?.company_status === "expired" ? "Piano scaduto" : user?.plan === "trial" ? "Prova gratuita" : "Abbonamento non attivo"}</h2>
            <p className="text-sm text-stone-600 mt-1">{user?.company_status === "expired" ? "Il tuo piano è scaduto. Rinnova o scegli un piano per riattivare l'accesso." : "Scegli un piano qui sotto per attivare o rinnovare l'accesso."}{user?.plan_expires_at ? ` — scadenza: ${new Date(user.plan_expires_at).toLocaleDateString("it-IT")}` : ""}</p>
          </div>
        )}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-sm bg-brand flex items-center justify-center"><Leaf className="text-white" size={18} strokeWidth={1.5} /></div>
            <span className="font-serif text-2xl text-stone-900">Q<span className="text-brand">COST</span></span>
          </div>
          {isActive ? (
            <button data-testid="billing-back" onClick={() => navigate("/")} className="text-sm text-stone-500 flex items-center gap-2 hover:text-brand"><ArrowLeft size={16} /> Torna alla dashboard</button>
          ) : (
            <button data-testid="billing-logout" onClick={() => { logout(); navigate("/login"); }} className="text-sm text-stone-500 flex items-center gap-2 hover:text-alert"><LogOut size={16} /> Esci</button>
          )}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 mb-2">{isActive ? "Il tuo abbonamento" : "Attiva il tuo abbonamento"}</h1>
        <p className="text-sm text-stone-500 mb-10">
          {isActive
            ? <>Ciao {user?.name}, <b>{user?.company_name}</b> è su un piano attivo. Passa a un piano superiore quando vuoi.</>
            : <>Ciao {user?.name}, scegli un piano per attivare <b>{user?.company_name}</b>.</>}
        </p>
        <div className="flex items-center gap-2 mb-8" data-testid="cycle-toggle">
          <button data-testid="cycle-monthly" onClick={() => setCycle("monthly")}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cycle === "monthly" ? "bg-brand text-white" : "bg-white border border-stone-300 text-stone-600 hover:bg-stone-50"}`}>Mensile</button>
          <button data-testid="cycle-annual" onClick={() => setCycle("annual")}
            className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cycle === "annual" ? "bg-brand text-white" : "bg-white border border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
            Annuale
            <span data-testid="annual-best-badge" className="absolute -top-2.5 -right-2 text-[9px] uppercase tracking-wider font-bold text-white bg-brand-accent px-1.5 py-0.5 rounded-full shadow">Più conveniente</span>
          </button>
          {cycle === "annual" && <span className="text-xs text-brand font-semibold ml-2">2 mesi gratis</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p) => {
            const isCurrent = isActive && currentPlan === p.key;
            const priceEur = cycle === "annual" ? p.price_annual_eur : p.price_eur;
            return (
              <div key={p.key} data-testid={`plan-${p.key}`} className={`bg-white border rounded-sm p-6 flex flex-col relative ${isCurrent ? "border-brand ring-1 ring-brand/40" : "border-stone-200"}`}>
                {isCurrent && <span data-testid={`plan-current-badge-${p.key}`} className="absolute top-4 right-4 text-[10px] uppercase tracking-wider font-bold text-brand bg-brand/10 px-2 py-1 rounded-full">Piano attuale</span>}
                <h3 className="font-serif text-3xl text-stone-900">{p.name}</h3>
                <p className="font-serif text-4xl font-light text-brand mt-3">{euro(priceEur)}<span className="text-sm text-stone-400">/{cycle === "annual" ? "anno" : "mese"}</span></p>
                {p.annual_savings_eur > 0 && (
                  <p data-testid={`plan-savings-${p.key}`} className="text-xs font-semibold text-brand bg-brand/10 rounded-full px-2.5 py-1 mt-2 self-start">
                    {cycle === "annual" ? `Risparmi ${euro(p.annual_savings_eur)}/anno` : `Annuale: −${euro(p.annual_savings_eur)} (2 mesi gratis)`}
                  </p>
                )}
                <p className="text-sm text-stone-600 mt-3 flex items-center gap-2"><Check size={16} className="text-brand" /> Fino a {p.max_users} utenti</p>
                <button data-testid={`subscribe-${p.key}`} disabled={loading === p.key || isCurrent || !stripeReady} onClick={() => subscribe(p.key)}
                  className="mt-6 rounded-full bg-brand text-white py-3 text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                  {isCurrent ? "Piano attuale" : loading === p.key ? "Attendere…" : isActive ? "Passa a questo piano" : "Abbonati"}
                </button>
              </div>
            );
          })}
        </div>
        {!stripeReady && (
          <p data-testid="stripe-not-configured" className="text-sm text-alert bg-alert/10 border border-alert/30 rounded-sm px-4 py-3 mt-8">
            I pagamenti non risultano configurati per questo ambiente. Contatta l'assistenza per attivarli.
          </p>
        )}
        <p className="text-xs text-stone-400 mt-8">Pagamento sicuro tramite Stripe. Carta di test: 4242 4242 4242 4242.</p>
      </div>
    </div>
  );
}
